import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  Box,
  Button,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import React from "react";
import {
  useLazyDeleteProductQuery,
  useLazyGetProductsQuery,
  useUpdateProductMutation,
  useUpdateProductStatusMutation,
} from "../../service/product";
import { showError, showToast } from "../../constants/toasts";
import { Product } from "../../types/General";
import dayjs from "dayjs";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import CustomPagination from "../../components/CustomPagination";
import Loader from "../../constants/Loader";

const ManageProducts = () => {
  const navigate = useNavigate();
  const [getProducts] = useLazyGetProductsQuery();
  const [deleteProduct] = useLazyDeleteProductQuery();
  const [changeStatus] = useUpdateProductStatusMutation();

  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [count, setCount] = useState(10);
  const [page, setPage] = useState(1);

  const getProductApi = async () => {
    setLoading(true);
    try {
      const response = await getProducts({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setProducts(response?.data?.list || []);
        setCount(response?.data?.total);
      } else {
        setProducts([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleStatusChange = async (id: string, status: any) => {
    try {
      const response = await changeStatus({ id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        getProductApi();
      }
    } catch (error: any) {
      console.error(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const deleteProductApi = async () => {
    setLoading(true);
    try {
      const response = await deleteProduct({ id: selectedId }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        showToast(response?.message || "");
        getProductApi();
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getProductApi();
  }, []);

  return (
    <>
      <Loader isLoad={loading} />

      <div className="main_title">
        <h1>Manage Products</h1>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary">Upload CSV</Button>
            <Button className="btn btn_primary">Export CSV</Button>
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/add-product")}
            >
              <AddIcon /> Add Products
            </Button>
          </div>
        </div>

        <TableContainer className="table_container" sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Product Detail</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>

                {/* ⭐ NEW HEADER */}
                <TableCell>No. of Questions</TableCell>

                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {products?.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item?.prodId || "-"}</TableCell>
                  <TableCell>
                    <Box className="user_block click_action">
                      <figure>
                        <img
                          src={item?.variants?.[0]?.media?.[0]?.url}
                          alt={item?.name}
                        />
                      </figure>
                      <p>
                        <b>{item.name}</b>
                      </p>
                      {/* {item.badge && (
                        <span className="sidebar_badge">{item.badge}</span>
                      )} */}
                    </Box>
                  </TableCell>
                  <TableCell>{item?.category?.name || "-"}</TableCell>
                  <TableCell>AED {"0"}</TableCell>
                  <TableCell>{"-"}</TableCell>
                  <TableCell>
                    <SwitchToggle
                      checked={item?.isActive ? true : false}
                      onChange={() =>
                        handleStatusChange(
                          item?.id,
                          item?.isActive ? false : true
                        )
                      }
                    />
                  </TableCell>

                  <TableCell>
                    {item.createdAt
                      ? dayjs(item.createdAt).format("DD MMMM YYYY")
                      : "-"}
                  </TableCell>

                  <TableCell>
                    <Box className="table_actions v2">
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-products/view/${item?.id}`)
                        }
                      >
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>

                      <IconButton
                        onClick={() => navigate(`/edit-product/${item?.id}`)}
                      >
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>

                      <IconButton
                        onClick={() => {
                          setSelectedId(item?.id);
                          setOpenModal(true);
                        }}
                      >
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {products?.length ? (
          <CustomPagination
            currentPage={page - 1}
            totalPages={Math.ceil(count / 10)}
            count={count}
            pageSize={10}
            onPageChange={(page) => setPage(page + 1)}
          />
        ) : null}

        <ConfirmationModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          text="Are you sure you want to delete this product?"
          handleSubmit={deleteProductApi}
        />
      </div>
    </>
  );
};

export default ManageProducts;
